import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Plus, StickyNote, Trash2, X, Info, Search, Flag, Check, Minus, ChevronRight, ChevronDown } from 'lucide';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { api } from '../api.js';
import { lucideIcon } from '../icon.js';
import base from '../styles/base.css?inline';

/**
 * lab-pakketten — welke analyses het lab op welk mengmonster uitvoert.
 *
 * Een matrix en geen lijst per mengmonster: je bestelt zelden één analyse op één monster.
 * Meestal zet je hetzelfde pakket op alles, met hier en daar een uitzondering, en dat lees je
 * af aan een kolom vinkjes. Zo werkt het ook in TerraBase, waar deze gebruikers vandaan komen.
 *
 * De kolommen zijn geen instelling die we bewaren: ze volgen uit wat er ergens aanstaat, plus
 * wat je deze sessie hebt toegevoegd. Daardoor klopt de matrix ook na herladen, en verdwijnt
 * een kolom vanzelf zodra het laatste vinkje eruit gaat.
 *
 * De pakketten komen uit de productlijst van het lab (GetProductList), per klantcode
 * opgehaald en gecachet — dezelfde lijst waar de urgenties en afspraken uit komen. In de kiezer
 * staan ze per groep, zoals in ATMIS: Grond [GRN], Grond (AS3000) [AG3], Afvalwater [AFW], ...
 * Dezelfde naam onder twee groepen is een ander pakket met een andere prijs (GRN-PFAS en
 * AG3-PFAS), dus de groep staat er altijd bij. Wat er ín een pakket zit haalt de i-knop op
 * (GetSuite, bewaard op de server); puur informatief, het lab heeft alleen de code nodig.
 *
 * Elke groep hoort bij een monstersoort van SGS, en een analysemonster gaat als één monstersoort
 * het lab in. Pakketten die niet samen op één monster kunnen, laat de matrix daarom niet samen
 * aanvinken; bij versturen rekent de server de monstersoort opnieuw uit.
 *
 * De rijen komen uit het mandje van Samenstelling (`mengmonsters`) en niet uit de bewaarde
 * opdracht: een analysemonster dat je net hebt gemaakt staat er dan meteen, ook al is het nog
 * niet bewaard. Pakketten aanvinken kan pas als het bewaard is -- het pakket hangt aan het
 * analysemonster in de backend. Wat er aan de mengmonsters zelf verandert (notitie,
 * nieuw, weg) meldt dit onderdeel aan de pagina; die laat het mandje het doen, zodat er één
 * plek is die de mengmonsters beheert.
 *
 * Events: mm-nieuw, mm-verwijder, mm-notitie (detail: { code, ... }),
 * en order-changed na het opslaan van pakketten. Hernoemen gaat via de functie `hernoem`, omdat
 * het antwoord (lukt het, of bestaat de naam al) meteen bij het veld moet staan.
 */
export class LabPakketten extends LitElement {
  static properties = {
    order: { type: Object },
    /** Het mandje: [{ code, notitie, potjes: [{ id, naam, boringNaam, van, tot }] }]. */
    mengmonsters: { attribute: false },
    /** (code) => string[] -- de mengregels die dit mengmonster breekt. */
    waarschuwingen: { attribute: false },
    /** (code, naam) => string|null -- hernoemen; geeft een melding als het niet kan. */
    hernoem: { attribute: false },
    _hernoemt: { state: true },      // code van het mengmonster waarvan de naam openstaat
    _hernoemFout: { state: true },
    /** Verstuurde opdracht: kijken mag, wijzigen niet meer. */
    vergrendeld: { type: Boolean },
    _kolommen: { state: true },      // pakketten die je deze sessie hebt toegevoegd
    _kiezerOpen: { state: true },
    _zoek: { state: true },
    _resultaten: { state: true },
    _categorieen: { state: true },
    _categorie: { state: true },
    _groepen: { state: true },       // [{ code, name, sampleTypeCode, count }] van deze koppeling
    _groep: { state: true },         // weergave in de kiezer: '' = alle groepen, 'FAV' = favorieten, 'SEL' = aangevinkte groepen
    _filter: { state: true },        // aangevinkt in de boom: Map groepcode → null (hele groep) of Set van categoriecodes
    _openGroepen: { state: true },   // opengeklapte groepen in de boom
    _catsPerGroep: { state: true },  // Map groepcode → [{ code, name, count }], opgehaald bij openklappen
    _voorkeur: { state: true },      // monstersoort die bij stap 1 hoort (grond → 11, AS3000 → 43)
    _info: { state: true },          // Map: sleutel → { bezig, fout, data } (inhoud van een pakket)
    _infoOpen: { state: true },      // sleutel van het pakket waarvan de inhoud openstaat
    _selectie: { state: true },      // aangevinkte pakketten in de kiezer, over groepen heen
    _favorieten: { state: true },    // vaste pakketten van deze gebruiker (bewaard als voorkeur)
    _melding: { state: true },       // na toevoegen: wat er niet overal bij kon
    _lokaal: { state: true },        // Map analysemonster-id → pakketten zoals net aangeklikt, nog niet terug van de server
    _zoekt: { state: true },
    _zoekfout: { state: true },
    _bezig: { state: true },
    _fout: { state: true },
    _notitieVoor: { state: true },   // het analysemonster waarvan de notitie openstaat
    _notitieTekst: { state: true },
  };

  constructor() {
    super();
    this.order = null;
    this._kolommen = [];
    this._kiezerOpen = false;
    this._zoek = '';
    this._resultaten = [];
    this._categorieen = [];
    this._categorie = '';
    this._groepen = [];
    this._groep = undefined;
    this._filter = new Map();
    this._openGroepen = new Set();
    this._catsPerGroep = new Map();
    this._voorkeur = '';
    this._info = new Map();
    this._infoOpen = null;
    this._selectie = [];
    this._favorieten = null;
    this._melding = '';
    this._lokaal = new Map();
    this._zoekt = false;
    this._zoekfout = null;
    this._bezig = false;
    this._fout = null;
    this._notitieVoor = null;
    this._notitieTekst = '';
    this._meetSchuif = this._meetSchuif.bind(this);
  }

  disconnectedCallback() {
    clearTimeout(this._zoekTimer);
    clearTimeout(this._verversTimer);
    this._meter?.disconnect();
    this._meter = null;
    super.disconnectedCallback();
  }

  updated(changed) {
    // De groepen één keer per koppeling: de matrix heeft ze nodig om te tonen als welke groep
    // elk analysemonster naar het lab gaat, ook als de kiezer nog niet open is geweest.
    const koppeling = this.order?.labConnectionId;
    if (changed?.has('order') && koppeling && this._groepenVoor !== koppeling) {
      this._groepenVoor = koppeling;
      api.getLabPackages(koppeling, {
        matrix: this.order.sampleType || 'grond', certificateFormat: this.order.certificateFormat || '', limit: 1,
      }).then(res => {
        this._groepen = res.groups || [];
        this._voorkeur = res.preferredSampleTypeCode || '';
      }).catch(() => {});
    }
    const vak = this.renderRoot?.querySelector('.tabelvak');
    if (vak && !this._meter) {
      // Ook meten als het venster of de inhoud verandert: een kolom erbij kan de tabel
      // breder maken dan het scherm zonder dat er iets gescrold is.
      this._meter = new ResizeObserver(() => this._meetSchuif());
      this._meter.observe(vak);
    }
    this._meetSchuif();
  }

  /**
   * De schaduwen op de vaste kolommen laten zien dat er iets achter zit -- dus alleen als dat
   * ook zo is. Staan ze er altijd, dan betekenen ze niets en zie je op een korte tabel een
   * rand waar geen rand hoort.
   *
   * Buiten Lit om: dit gebeurt bij elke scrollstap, en daarvoor hoeft de tabel niet opnieuw
   * getekend te worden.
   */
  _meetSchuif() {
    const vak = this.renderRoot?.querySelector('.tabelvak');
    if (!vak) return;
    const speling = vak.scrollWidth - vak.clientWidth;
    vak.classList.toggle('links-verborgen', vak.scrollLeft > 1);
    vak.classList.toggle('rechts-verborgen', speling > 1 && vak.scrollLeft < speling - 1);
  }

  /** De bewaarde analysemonsters: die dragen de pakketten. */
  get _monsters() {
    return this.order?.analysisSamples || [];
  }

  /**
   * Eén rij per mengmonster uit het mandje, met het bewaarde analysemonster erbij (op id, want
   * de naam kun je wijzigen).
   * Zonder mandje -- dat gebeurt alleen als de pagina het nog niet heeft -- de bewaarde.
   */
  get _rijen() {
    const opId = new Map(this._monsters.map(s => [s.id, s]));
    if (!Array.isArray(this.mengmonsters)) {
      return this._monsters.map(s => ({
        code: s.name, notitie: s.reason || '', sample: s,
        potjes: (s.parts || []).map(p => ({ id: p.monsterId || p.id, naam: p.observations?.naam || p.barcode || '—' })),
      }));
    }
    return this.mengmonsters.map(m => ({ ...m, sample: (m.id && opId.get(m.id)) || null }));
  }

  /**
   * De naam ter plekke wijzigen: Enter of wegklikken bewaart, Escape laat het. Lukt het niet
   * (leeg, of de naam bestaat al), dan blijft het veld open met de reden eronder.
   */
  _openHernoem(rij) {
    this._hernoemt = rij.code;
    this._hernoemFout = '';
    this.updateComplete.then(() => {
      const veld = this.renderRoot.querySelector('.naamveld');
      veld?.focus();
      veld?.select();
    });
  }

  _bewaarHernoem(code, naam) {
    if (this._hernoemt !== code) return;
    const fout = this.hernoem?.(code, naam) || null;
    if (fout) { this._hernoemFout = fout; return; }
    this._hernoemt = null;
    this._hernoemFout = '';
  }

  _meld(naam, detail = {}) {
    this.dispatchEvent(new CustomEvent(naam, { detail, bubbles: true, composed: true }));
  }

  /**
   * Een analysemonster weggooien. Vragen als er iets in zit: de potjes komen weer vrij, maar de
   * gekozen pakketten zijn dan weg.
   */
  async _verwijderRij(rij) {
    const pakketten = this._pakkettenVan(rij.sample).length;
    if (rij.potjes.length || pakketten) {
      const delen = [
        rij.potjes.length ? `${rij.potjes.length} ${rij.potjes.length === 1 ? 'potje komt' : 'potjes komen'} weer vrij` : '',
        pakketten ? `${pakketten} ${pakketten === 1 ? 'gekozen pakket verdwijnt' : 'gekozen pakketten verdwijnen'}` : '',
      ].filter(Boolean);
      const ok = await confirmDialog({
        title: `${rij.code} weggooien?`,
        message: `${delen.join(' en ')}.`,
        confirmLabel: 'Ja, weggooien',
        danger: true,
      });
      if (!ok) return;
    }
    this._meld('mm-verwijder', { code: rij.code });
  }

  /**
   * Waarop twee pakketten dezelfde zijn: de code, en anders de naam.
   *
   * Vergelijken op code, want namen zijn niet uniek -- "Lood" bestaat als GRN-PB én als
   * AG3-PB. Pakketten uit de bouwfase dragen alleen een naam; die vielen door de codevergelijking
   * uit de matrix en waren daardoor niet meer weg te klikken, terwijl de controle er wel over
   * klaagde. Vandaar de terugval op de naam.
   */
  _sleutel(pkg) {
    return pkg?.code ? `code:${pkg.code}` : `naam:${String(pkg?.name || '').toLowerCase()}`;
  }

  /** Elk pakket dat ergens aanstaat, plus wat je zelf hebt toegevoegd. */
  get _zichtbareKolommen() {
    const kolommen = new Map(this._kolommen.map(p => [this._sleutel(p), p]));
    for (const sample of this._monsters) {
      for (const pkg of this._pakkettenVan(sample)) {
        const sleutel = this._sleutel(pkg);
        if (!kolommen.has(sleutel)) kolommen.set(sleutel, pkg);
      }
    }
    return [...kolommen.values()];
  }

  /** Staat dit pakket aan bij dit monster? */
  _staatAan(sample, pkg) {
    return this._pakkettenVan(sample).some(gekozen => this._sleutel(gekozen) === this._sleutel(pkg));
  }

  /**
   * De kolom vasthouden zodra je er iets mee doet. Een kolom staat er omdat het pakket ergens aan
   * staat of omdat je hem deze sessie hebt toegevoegd; na herladen alleen om de eerste reden. Zet
   * je het pakket dan overal uit, dan verdween de kolom meteen. Weghalen hoort bij het kruisje.
   */
  _houdKolom(pkg) {
    if (this._kolommen.some(k => this._sleutel(k) === this._sleutel(pkg))) return;
    // Alle zichtbare kolommen in hun huidige volgorde vastleggen, anders springt deze naar voren
    // (kolommen uit deze sessie staan vooraan).
    this._kolommen = this._zichtbareKolommen.map(k => this._bewaarvorm(k));
  }

  async _wissel(sample, pkg) {
    this._houdKolom(pkg);
    const huidig = this._pakkettenVan(sample);
    const packages = this._staatAan(sample, pkg)
      ? huidig.filter(gekozen => this._sleutel(gekozen) !== this._sleutel(pkg))
      : [...huidig, this._bewaarvorm(pkg)];
    await this._opslaan([[sample, { packages }]]);
  }

  /** Wat er van een pakket bewaard wordt: code en naam, plus groep en monstersoorten als we die kennen. */
  _bewaarvorm(pkg) {
    return {
      code: pkg.code, name: pkg.name,
      ...(pkg.groupCode ? { groupCode: pkg.groupCode } : {}),
      ...(pkg.sampleTypes?.length ? { sampleTypes: pkg.sampleTypes } : {}),
    };
  }

  /**
   * De monstersoorten die alle pakketten van dit analysemonster gemeen hebben, of null als we
   * dat niet weten (pakketten van voor de groepen, zonder soorten). Zie resolveSampleType in de
   * backend; dit is dezelfde regel, voor de waarschuwing vooraf.
   */
  _gemeen(packages) {
    let gemeen = null;
    for (const pkg of packages || []) {
      if (!pkg.sampleTypes?.length) continue;
      gemeen = gemeen ? gemeen.filter(t => pkg.sampleTypes.includes(t)) : [...pkg.sampleTypes];
    }
    return gemeen;
  }

  /** Kan dit pakket er bij dit analysemonster nog bij, zonder dat ze niet op één monster passen? */
  _past(sample, pkg) {
    if (!pkg.sampleTypes?.length || this._staatAan(sample, pkg)) return true;
    const gemeen = this._gemeen(this._pakkettenVan(sample));
    return !gemeen || gemeen.some(t => pkg.sampleTypes.includes(t));
  }

  /** Als welke groep dit analysemonster naar het lab gaat (uit de gekozen pakketten), of null. */
  _groepVan(sample) {
    const gemeen = this._gemeen(this._pakkettenVan(sample));
    if (!gemeen) return null;
    if (!gemeen.length) return { conflict: true };
    const soort = gemeen.includes(this._voorkeur) ? this._voorkeur
      : (() => {
        const eerste = this._pakkettenVan(sample).find(p => p.groupCode);
        const eigen = this._groepen.find(g => g.code === eerste?.groupCode)?.sampleTypeCode;
        return eigen && gemeen.includes(eigen) ? eigen : [...gemeen].sort()[0];
      })();
    return this._groepen.find(g => g.sampleTypeCode === soort) || { name: `monstersoort ${soort}` };
  }

  /** De pakketten van een analysemonster zoals je ze net hebt aangeklikt, anders zoals bewaard. */
  _pakkettenVan(sample) {
    if (!sample) return [];
    return this._lokaal.get(sample.id) ?? sample.packages ?? [];
  }

  /**
   * Wijzigingen meteen tonen en op de achtergrond bewaren.
   *
   * Eerder blokkeerde elke klik de hele matrix tot het analysemonster bewaard was én de hele
   * opdracht opnieuw was opgehaald; je zat bij elk vinkje te wachten. Nu staat het vinkje direct
   * goed. De opslagacties gaan in volgorde (een rij), zodat snel achter elkaar klikken op hetzelfde
   * monster niet in de verkeerde volgorde bij de server aankomt. De opdracht halen we pas op als
   * er even niets meer gebeurt, en dan één keer. Lukt bewaren niet, dan zetten we alles terug
   * naar wat de server heeft en zeggen we het.
   */
  _opslaan(wijzigingen) {
    if (this.vergrendeld || !wijzigingen.length) return Promise.resolve();
    this._fout = null;
    const lokaal = new Map(this._lokaal);
    for (const [sample, velden] of wijzigingen) {
      if (velden.packages) lokaal.set(sample.id, velden.packages);
    }
    this._lokaal = lokaal;
    this._openstaand = (this._openstaand || 0) + 1;
    this._rij = (this._rij || Promise.resolve()).then(async () => {
      for (const [sample, velden] of wijzigingen) {
        await api.updateLabAnalysisSample(sample.id, velden);
      }
    }).catch((e) => {
      this._fout = e.message || 'Pakket wijzigen mislukt';
      this._mislukt = true;
    }).finally(() => {
      this._openstaand -= 1;
      if (this._openstaand === 0) this._verversStraks();
    });
    return this._rij;
  }

  /** De opdracht één keer opnieuw ophalen zodra er even niets meer bewaard wordt. */
  _verversStraks() {
    clearTimeout(this._verversTimer);
    this._verversTimer = setTimeout(async () => {
      if (this._openstaand) return;
      try {
        const res = await api.getLabOrder(this.order.id);
        if (this._openstaand) return; // intussen weer geklikt: de volgende ronde ververst
        // De pagina eromheen houdt de opdracht vast; die moet mee, anders lopen de tellers
        // achter de tabbladen achter op wat je net hebt aangevinkt.
        this.dispatchEvent(new CustomEvent('order-changed', {
          detail: { labOrder: res.labOrder }, bubbles: true, composed: true,
        }));
        this._lokaal = new Map();
        this._mislukt = false;
      } catch (e) {
        if (this._mislukt) this._lokaal = new Map();
        this._fout = this._fout || e.message || 'Opdracht verversen mislukt';
      }
    }, 600);
  }

  /**
   * De stand van een kolom voor het vinkje in de kop: 'aan' (bij alle analysemonsters waar het
   * pakket past), 'half' (bij een deel) of 'uit'.
   */
  _kolomStand(pkg) {
    const kan = this._monsters.filter(s => this._past(s, pkg));
    const aan = kan.filter(s => this._staatAan(s, pkg)).length;
    if (!aan) return 'uit';
    return aan === kan.length ? 'aan' : 'half';
  }

  /** Een heel mengmonster aan- of uitzetten voor dit pakket: de kolomkop is ook een knop. */
  async _wisselKolom(pkg) {
    this._houdKolom(pkg);
    // Alleen de analysemonsters waar het pakket kan staan; de rest blijft zoals hij is.
    const kan = this._monsters.filter(s => this._past(s, pkg));
    const allemaal = kan.length && kan.every(s => this._staatAan(s, pkg));
    await this._opslaan(kan.map(sample => [sample, {
      packages: allemaal
        ? this._pakkettenVan(sample).filter(g => this._sleutel(g) !== this._sleutel(pkg))
        : this._staatAan(sample, pkg) ? this._pakkettenVan(sample) : [...this._pakkettenVan(sample), this._bewaarvorm(pkg)],
    }]));
  }

  /**
   * Een kolom weghalen. Staat het pakket ergens aan, dan gooi je die keuzes weg -- vandaar de
   * vraag. Staat het nergens aan, dan is de kolom alleen iets wat je deze sessie hebt
   * opgezocht en kan hij zonder meer weg.
   */
  async _verwijderKolom(pkg, e) {
    e?.stopPropagation();
    const aan = this._monsters.filter(s => this._staatAan(s, pkg));
    if (aan.length) {
      const ok = await confirmDialog({
        title: `${pkg.name} weghalen?`,
        message: `Dit pakket staat aan bij ${aan.length} ${aan.length === 1 ? 'mengmonster' : 'mengmonsters'}. Die keuze gaat weg.`,
        confirmLabel: 'Ja, weghalen',
        danger: true,
      });
      if (!ok) return;
    }
    this._kolommen = this._kolommen.filter(k => this._sleutel(k) !== this._sleutel(pkg));
    await this._opslaan(aan.map(sample => [
      sample, { packages: this._pakkettenVan(sample).filter(g => this._sleutel(g) !== this._sleutel(pkg)) },
    ]));
  }

  /**
   * Waarom juist deze potjes samen gaan. Dat staat nergens anders: de potjes vertellen wat er
   * in zit, niet waarom ze één pot zijn geworden. Een half jaar later, bij een vraag over de
   * uitslag, is dat het eerste wat je wilt weten.
   */
  _openNotitie(rij) {
    this._notitieVoor = rij;
    this._notitieTekst = rij.notitie || '';
  }

  _bewaarNotitie() {
    const rij = this._notitieVoor;
    const tekst = this._notitieTekst.trim();
    this._notitieVoor = null;
    if ((rij.notitie || '') !== tekst) this._meld('mm-notitie', { code: rij.code, tekst });
  }

  _renderNotitievenster() {
    const rij = this._notitieVoor;
    if (!rij) return '';
    return html`
      <div class="overlay" @click=${e => { if (e.target === e.currentTarget) this._notitieVoor = null; }}>
        <div class="venster notitie-venster">
          <header>
            <div><strong>Notitie bij ${rij.code}</strong></div>
            <button class="sluit" @click=${() => { this._notitieVoor = null; }}>${unsafeSVG(lucideIcon(X, 16))}</button>
          </header>
          <div class="venster-lijf">
            <textarea rows="5" .value=${this._notitieTekst}
                      @input=${e => { this._notitieTekst = e.target.value; }}></textarea>
            <div class="notitieknoppen">
              <button class="tekstknop" @click=${() => { this._notitieVoor = null; }}>Annuleren</button>
              <button class="knop vol" ?disabled=${this._bezig} @click=${() => this._bewaarNotitie()}>Notitie opslaan</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _openKiezer() {
    this._kiezerOpen = true;
    this._zoek = '';
    this._categorie = '';
    this._zoekfout = null;
    this._infoOpen = null;
    this._selectie = [];
    this._melding = '';
    // Nog geen groep: de server opent de groep die bij stap 1 past.
    this._groep = undefined;
    this._filter = new Map();
    this._openGroepen = new Set();
    this._laadFavorieten();
    this._zoeken();
    this._opEscape = (e) => { if (e.key === 'Escape') this._sluitKiezer(); };
    window.addEventListener('keydown', this._opEscape);
  }

  _sluitKiezer() {
    this._kiezerOpen = false;
    this._selectie = [];
    clearTimeout(this._zoekTimer);
    window.removeEventListener('keydown', this._opEscape);
  }

  /** Alle groepen ('') of Favorieten ('FAV'): wat in de boom aanstaat gaat dan uit. */
  _kiesGroep(code) {
    this._groep = code;
    this._filter = new Map();
    this._infoOpen = null;
    if (code !== 'FAV') this._zoeken();
  }

  /** De stand van een groep in de boom: 'aan', 'half' (een deel van de categorieën) of 'uit'. */
  _groepStand(code) {
    if (!this._filter.has(code)) return 'uit';
    return this._filter.get(code) === null ? 'aan' : 'half';
  }

  _categorieAan(groep, cat) {
    if (!this._filter.has(groep)) return false;
    const set = this._filter.get(groep);
    return set === null || set.has(cat);
  }

  /** Na een wijziging in de boom: de weergave volgt, en de lijst wordt opnieuw opgehaald. */
  _filterGewijzigd(filter) {
    this._filter = filter;
    this._groep = filter.size ? 'SEL' : '';
    this._infoOpen = null;
    this._zoeken();
  }

  /** Een hele groep aan of uit (half telt als aan, dus die gaat uit). */
  _wisselGroep(code) {
    const filter = new Map(this._groep === 'SEL' ? this._filter : []);
    if (filter.has(code)) filter.delete(code); else filter.set(code, null);
    this._filterGewijzigd(filter);
  }

  /**
   * Eén categorie binnen een groep aan of uit, zoals in ATMIS. Staat de hele groep aan, dan gaan
   * alle andere categorieën aan behalve deze; staan ze allemaal aan, dan is het weer de hele groep.
   */
  _wisselCategorie(groep, cat) {
    const alle = (this._catsPerGroep.get(groep) || []).map(c => c.code);
    const filter = new Map(this._groep === 'SEL' ? this._filter : []);
    let set = filter.has(groep) ? filter.get(groep) : new Set();
    set = set === null ? new Set(alle) : new Set(set);
    if (set.has(cat)) set.delete(cat); else set.add(cat);
    if (!set.size) filter.delete(groep);
    else filter.set(groep, alle.length && alle.every(c => set.has(c)) ? null : set);
    this._filterGewijzigd(filter);
  }

  /** Een groep open- of dichtklappen; de categorieën worden de eerste keer opgehaald. */
  async _wisselOpen(code, e) {
    e?.stopPropagation();
    const open = new Set(this._openGroepen);
    if (open.has(code)) { open.delete(code); this._openGroepen = open; return; }
    open.add(code);
    this._openGroepen = open;
    if (this._catsPerGroep.has(code)) return;
    try {
      const res = await api.getLabPackages(this.order.labConnectionId, {
        matrix: this.order.sampleType || 'grond', certificateFormat: this.order.certificateFormat || '',
        group: code, limit: 1,
      });
      this._catsPerGroep = new Map(this._catsPerGroep).set(code, res.categories || []);
    } catch (_) {
      this._catsPerGroep = new Map(this._catsPerGroep).set(code, []);
    }
  }

  /**
   * Zoeken gebeurt op de server, niet in de browser: deze klant heeft ~1900 pakketten, en daar
   * kiest iemand er hooguit vijf uit. Binnen Favorieten zoeken we in de eigen lijst.
   */
  _typt(waarde) {
    this._zoek = waarde;
    if (this._groep === 'FAV') return;
    clearTimeout(this._zoekTimer);
    this._zoekTimer = setTimeout(() => this._zoeken(), 250);
  }

  async _zoeken() {
    const connectionId = this.order?.labConnectionId;
    if (!connectionId) {
      this._zoekfout = 'Deze opdracht heeft nog geen laboratorium.';
      this._resultaten = [];
      this._categorieen = [];
      this._categorie = '';
      return;
    }
    this._zoekt = true;
    this._zoekfout = null;
    const vraag = (group) => api.getLabPackages(connectionId, {
      matrix: this.order.sampleType || 'grond',
      certificateFormat: this.order.certificateFormat || '',
      query: this._zoek,
      group,
      // Een hele groep in één keer: Grond heeft er ~250, en daar blader je doorheen.
      limit: 500,
    });
    const beurt = (this._zoekBeurt || 0) + 1;
    this._zoekBeurt = beurt;
    try {
      let res;
      if (this._groep === 'SEL') {
        // Eén vraag per aangevinkte groep (elke groep heeft zijn eigen monstersoort); staat maar
        // een deel van de categorieën aan, dan filteren we hier.
        const codes = [...this._filter.keys()];
        const delen = await Promise.all(codes.map(code => vraag(code)));
        const cats = new Map(this._catsPerGroep);
        codes.forEach((code, i) => { if (delen[i].categories) cats.set(code, delen[i].categories); });
        this._catsPerGroep = cats;
        const packages = codes.flatMap((code, i) => {
          const set = this._filter.get(code);
          return (delen[i].packages || []).filter(p => set === null || set.has(p.categoryCode));
        });
        res = { ...delen[0], packages, categories: [] };
      } else {
        res = await vraag(this._groep);
      }
      // Een trager antwoord op een oudere vraag mag een nieuwere uitslag niet overschrijven.
      if (beurt !== this._zoekBeurt) return;
      this._resultaten = res.packages || [];
      this._categorieen = res.categories || [];
      if (res.groups) this._groepen = res.groups;
      if (res.preferredSampleTypeCode) this._voorkeur = res.preferredSampleTypeCode;
      if (this._groep === undefined) {
        // Bij openen: de groep van stap 1 staat aan en opengeklapt, zoals je hem in ATMIS openzet.
        this._filter = res.group ? new Map([[res.group, null]]) : new Map();
        this._groep = res.group ? 'SEL' : '';
        if (res.group) {
          this._openGroepen = new Set([res.group]);
          this._catsPerGroep = new Map(this._catsPerGroep).set(res.group, res.categories || []);
        }
      }
      // Een lege lijst zonder zoekterm betekent niet "niets gevonden" maar "deze matrix
      // kennen we nog niet bij het lab"; de server zegt er dan iets over.
      if (res.message) this._zoekfout = res.message;
    } catch (e) {
      this._zoekfout = e.message || 'Pakketten ophalen mislukt';
      this._resultaten = [];
      this._categorieen = [];
      this._categorie = '';
    } finally {
      if (beurt === this._zoekBeurt) this._zoekt = false;
    }
  }

  // ── Favorieten: per gebruiker op de server, zodat ze op elke computer gelden ──────────────

  static FAVORIETEN_PREF = 'lab.favorietePakketten';

  async _laadFavorieten() {
    if (this._favorieten) return;
    try {
      const lijst = (await api.getPreferences())?.[LabPakketten.FAVORIETEN_PREF];
      this._favorieten = Array.isArray(lijst) ? lijst.filter(p => p?.code) : [];
    } catch (_) {
      this._favorieten = [];
    }
  }

  _isFavoriet(pkg) {
    return (this._favorieten || []).some(f => f.code === pkg.code);
  }

  /** Aan of uit, en meteen bewaren. Het hele pakket gaat mee, zodat Favorieten zonder zoekvraag te tonen is. */
  async _wisselFavoriet(pkg, e) {
    e?.stopPropagation();
    const nu = this._favorieten || [];
    this._favorieten = this._isFavoriet(pkg)
      ? nu.filter(f => f.code !== pkg.code)
      : [...nu, { ...this._bewaarvorm(pkg), groupName: pkg.groupName || null, sampleTypeCode: pkg.sampleTypeCode || null }];
    try { await api.setPreference(LabPakketten.FAVORIETEN_PREF, this._favorieten); }
    catch (err) { console.error('favorieten opslaan mislukt:', err); }
  }

  // ── Selectie ──────────────────────────────────────────────────────────────────────────

  /** Staat dit pakket al als kolom in de opdracht? Dan hoeft (en kan) het niet nog eens. */
  _inOpdracht(pkg) {
    return this._zichtbareKolommen.some(k => this._sleutel(k) === this._sleutel(pkg));
  }

  _isGekozen(pkg) {
    return this._selectie.some(p => this._sleutel(p) === this._sleutel(pkg));
  }

  _wisselSelectie(pkg) {
    if (this._inOpdracht(pkg)) return;
    this._selectie = this._isGekozen(pkg)
      ? this._selectie.filter(p => this._sleutel(p) !== this._sleutel(pkg))
      : [...this._selectie, pkg];
  }

  /**
   * De selectie toevoegen als kolommen. Aanvinken per mengmonster doe je daarna zelf in de
   * matrix: welk pakket op welk mengmonster hoort, is een keuze per monster, niet voor allemaal.
   */
  _voegSelectieToe() {
    const nieuw = this._selectie.filter(p => !this._inOpdracht(p));
    this._kolommen = [...this._kolommen, ...nieuw.map(p => this._bewaarvorm(p))];
    this._sluitKiezer();
  }

  /** De inhoud van een pakket open- of dichtklappen; ophalen bij de eerste keer. */
  async _wisselInfo(pkg, e) {
    e?.stopPropagation();
    const sleutel = `${pkg.code}|${pkg.sampleTypeCode}`;
    if (this._infoOpen === sleutel) { this._infoOpen = null; return; }
    this._infoOpen = sleutel;
    if (this._info.get(sleutel)?.data) return;
    this._info = new Map(this._info).set(sleutel, { bezig: true });
    try {
      const res = await api.getLabPackageInfo(this.order.labConnectionId, { code: pkg.code, sampleType: pkg.sampleTypeCode });
      this._info = new Map(this._info).set(sleutel, { data: res.info });
    } catch (err) {
      this._info = new Map(this._info).set(sleutel, { fout: err.message || 'Inhoud ophalen mislukt' });
    }
  }

  /**
   * Wat er in een pakket zit, zoals het informatievenster in ATMIS.
   *
   * GetSuite geeft alleen de opbouw: onderdelen en bepalingen met code en omschrijving. Methode,
   * rapportagegrens, eenheid, levertermijn en prijs staan wel in ATMIS maar niet in de webservice
   * (nagekeken op GRN-STAP1, PUI-CU en PUI-MAL, 05-11-2026); dat zeggen we er dan ook bij.
   *
   * SGS zet bepalingen en voorbewerkingen door elkaar als onderdeel: bij PUI-CU (Koper) zijn de
   * 'onderdelen' Droge stof en Inweeg metalen destructie. Die herkennen we aan hun naam en tonen
   * we apart, op één regel: daar zit vaak juist het verschil (AG3-PFAS heeft een AS3000-
   * voorbewerking, GRN-PFAS niet).
   */
  _renderInfo(pkg) {
    const staat = this._info.get(`${pkg.code}|${pkg.sampleTypeCode}`);
    const kader = (inhoud) => html`<div class="pk-info">${inhoud}
      <div class="pk-info-noot">Methode, rapportagegrens, eenheid en prijs staan in het labportaal.</div></div>`;
    if (!staat || staat.bezig) return html`<div class="pk-info"><span class="pk-stil">Inhoud ophalen bij het lab…</span></div>`;
    if (staat.fout) return html`<div class="pk-info"><span class="pk-fout">${staat.fout}</span></div>`;
    const d = staat.data;
    const isVoorbewerking = (x) => /droge stof|inweeg|voorbewerking|malen|destructie|zeven|drogen|extractie|ontsluiting|homogenis/i
      .test(x?.description || '') && !(x.parts || []).length;
    const onderdelen = d.parts || [];
    const bepalingen = onderdelen.filter(x => !isVoorbewerking(x));
    const extra = new Map();
    for (const x of [...onderdelen.filter(isVoorbewerking), ...(d.analyses || []), ...onderdelen.flatMap(o => o.analyses || [])]) {
      const naam = x.description || x.code;
      if (naam) extra.set(x.code || naam, naam);
    }
    return kader(html`
      ${bepalingen.length ? html`
        <div class="pk-info-kop">Bepalingen (${bepalingen.length})</div>
        <div class="pk-bepalingen">
          ${bepalingen.map(b => html`<div class="pk-bepaling"><code>${b.code || ''}</code><span>${b.description || b.code}</span></div>`)}
        </div>` : html`<span class="pk-stil-donker">Het lab geeft voor dit pakket geen samenstelling mee.</span>`}
      ${extra.size ? html`<div class="pk-voorbewerking"><strong>Voorbewerking:</strong> ${[...extra.values()].join(' · ')}</div>` : ''}`);
  }

  render() {
    const kolommen = this._zichtbareKolommen;
    const rijen = this._rijen;
    const potjes = rijen.reduce((n, r) => n + r.potjes.length, 0);
    const kan = !this.vergrendeld;
    return html`
      <section class="paneel">
        <header class="kop">
          <span class="mv-caps">Analysemonsters &amp; pakketten</span>
          <span class="spacer"></span>
          <span class="telling">
            ${rijen.length} ${rijen.length === 1 ? 'analysemonster' : 'analysemonsters'} ·
            ${potjes} ${potjes === 1 ? 'potje' : 'potjes'}
          </span>
        </header>

        ${this._fout ? html`<p class="fout">${this._fout}</p>` : ''}
        ${this._melding ? html`<p class="melding">${this._melding}
          <button class="tekstknop" @click=${() => { this._melding = ''; }}>Sluiten</button></p>` : ''}

        ${!rijen.length ? html`
          <div class="leeg">
            <strong>Nog geen analysemonsters</strong>
            <span>Klik potjes aan in de boorprofielen bij Samenstelling, of maak hier een leeg analysemonster
                  en vul het daarna.</span>
          </div>
        ` : html`
          <div class="tabelvak" @scroll=${this._meetSchuif}>
            <table>
              <thead>
                <tr>
                  <th class="mm">Analysemonster</th>
                  <th class="matrixkolom">Monstertype</th>
                  ${kolommen.map(pkg => html`
                    <th class="pakket ${pkg.code ? '' : 'zondercode'}">
                      <button class="kolomknop" title=${pkg.code
                                ? `${pkg.name} (${pkg.code}) — aan of uit voor alle analysemonsters`
                                : `${pkg.name} — dit pakket heeft geen labcode en kan niet verstuurd worden; haal het weg en kies het opnieuw`}
                              @click=${() => this._wisselKolom(pkg)} ?disabled=${!kan}>
                        <span class="pakketnaam">${pkg.name}</span>
                        <code>${pkg.code || 'geen labcode'}</code>
                        ${pkg.groupCode ? html`<span class="groepnaam">${this._groepen.find(g => g.code === pkg.groupCode)?.name || pkg.groupCode}</span>` : ''}
                        ${(() => {
                          // Alles aan of uit voor dit pakket: aan, half (een deel) of uit.
                          const stand = this._monsters.length ? this._kolomStand(pkg) : 'uit';
                          return html`<span class="vinkje kolomvinkje ${stand}" role="checkbox"
                            aria-checked=${stand === 'aan' ? 'true' : stand === 'half' ? 'mixed' : 'false'}
                            aria-label=${`${pkg.name} voor alle analysemonsters`}></span>`;
                        })()}
                      </button>
                      ${kan ? html`
                      <button class="kolomweg" title="Deze kolom weghalen"
                              @click=${(e) => this._verwijderKolom(pkg, e)}>${unsafeSVG(lucideIcon(X, 12))}</button>` : ''}
                    </th>`)}
                  <th class="toevoegen">
                    ${kan ? html`
                      <button class="tekstknop groen" @click=${this._openKiezer}>+ Pakket</button>`
                      : html`<span class="slot">vastgelegd</span>`}
                  </th>
                </tr>
              </thead>
              <tbody>
                ${rijen.map(rij => this._renderRij(rij, kolommen, kan))}
              </tbody>
            </table>
          </div>
          ${!kolommen.length ? html`
            <p class="hint">Nog geen pakketten gekozen. Zoek er een bij het lab via <strong>+ Pakket</strong>.</p>
          ` : ''}
        `}

        ${kan ? html`
          <footer class="voet">
            <button class="hoofdknop" @click=${() => this._meld('mm-nieuw')}>
              ${unsafeSVG(lucideIcon(Plus, 14))} Analysemonster toevoegen
            </button>
          </footer>` : ''}
      </section>
      ${this._renderKiezer()}
      ${this._renderNotitievenster()}
    `;
  }

  /**
   * Eén analysemonster: waar het uit bestaat, en per pakket een vinkje.
   *
   * Het stipje is groen, of rood als het mengmonster een mengregel breekt; welke regel staat
   * eronder. De potjes staan hier vast: wat erin zit pas je aan bij Samenstelling.
   */
  _renderRij(rij, kolommen, kan) {
    const sample = rij.sample;
    const breekt = this.waarschuwingen?.(rij.code) || [];
    return html`
      <!-- Geen 'actief' mengmonster hier: dat hoort bij Samenstelling, waar je potjes aanklikt.
           Bij Pakketten liggen de mengmonsters vast. -->
      <tr>
        <td class="mm">
          <div class="mmkop">
            <span class="stip ${breekt.length ? 'rood' : ''}"></span>
            ${this._hernoemt === rij.code ? html`
              <input class="naamveld" .value=${rij.code} maxlength="40" aria-label="Naam van het mengmonster"
                     @keydown=${(e) => {
                       if (e.key === 'Enter') { e.preventDefault(); this._bewaarHernoem(rij.code, e.target.value); }
                       if (e.key === 'Escape') { this._hernoemt = null; this._hernoemFout = ''; }
                     }}
                     @blur=${(e) => this._bewaarHernoem(rij.code, e.target.value)}>
            ` : html`
              <!-- Klik op de naam om hem aan te passen, net als bij Samenstelling. -->
              ${kan ? html`
                <button class="mmnaam" title="Klik om de naam aan te passen" @click=${() => this._openHernoem(rij)}>${rij.code}</button>`
                : html`<span class="mmnaam">${rij.code}</span>`}`}
            ${kan || rij.notitie ? html`
              <button class="notitie ${rij.notitie ? 'gevuld' : ''}"
                      title=${rij.notitie ? `Notitie: ${rij.notitie}` : 'Notitie toevoegen — bijvoorbeeld waarom juist deze potjes samen gaan'}
                      @click=${() => this._openNotitie(rij)}>
                ${unsafeSVG(lucideIcon(StickyNote, 13))}
              </button>` : ''}
          </div>
          ${this._hernoemt === rij.code && this._hernoemFout ? html`<div class="naamfout">${this._hernoemFout}</div>` : ''}
          <div class="potjes">
            ${rij.potjes.length ? rij.potjes.map(p => html`
              <span class="potje" title=${`${p.boringNaam ? `${p.boringNaam} · ` : ''}${p.van ?? ''}–${p.tot ?? ''} cm · aanpassen bij Samenstelling`}>${p.naam}</span>`)
              : html`<small>nog geen potjes · klik ze aan bij Samenstelling</small>`}
          </div>
          ${breekt.length ? html`<ul class="waarsch">${breekt.map(w => html`<li>${w}</li>`)}</ul>` : ''}
        </td>
        ${this._renderMonstertype(rij, sample)}
        ${kolommen.map(pkg => {
          const past = !sample || this._past(sample, pkg);
          return html`
          <td class="vink">
            <button class="vinkje ${sample && this._staatAan(sample, pkg) ? 'aan' : ''} ${past ? '' : 'past-niet'}"
                    title=${!sample ? `${rij.code} wordt nog bewaard`
                      : !past ? `${pkg.name} (${pkg.code}) kan niet samen met de andere pakketten van ${rij.code} op één monster: ander soort monster bij het lab. Zet het op een eigen analysemonster.`
                      : `${pkg.name} voor ${rij.code}`}
                    ?disabled=${!kan || !sample || !past}
                    @click=${() => this._wissel(sample, pkg)}></button>
          </td>`;
        })}
        <td class="toevoegen">
          ${kan ? html`
            <button class="weg" title=${`${rij.code} weggooien`} @click=${() => this._verwijderRij(rij)}>
              ${unsafeSVG(lucideIcon(Trash2, 15))}
            </button>` : ''}
        </td>
      </tr>`;
  }

  /**
   * Als wat dit analysemonster naar het lab gaat. Met pakketten: de groep die uit die pakketten
   * volgt (AG3-PFAS maakt er Grond (AS3000) van). Zonder: het monstertype zoals het altijd was.
   */
  _renderMonstertype(rij, sample) {
    const groep = this._groepVan(sample);
    if (groep?.conflict) return html`<td class="matrixkolom"><span class="soort-fout">pakketten passen niet samen</span></td>`;
    if (groep) return html`<td class="matrixkolom" title="Zo gaat dit analysemonster naar het lab, afgeleid uit de gekozen pakketten">${groep.name}</td>`;
    return html`<td class="matrixkolom">${sample?.matrix === 'water' ? 'grondwater' : (sample?.matrix || (rij.potjes.some(p => p.matrix === 'water' || p.matrix === 'grondwater') ? 'grondwater' : 'grond'))}</td>`;
  }

  /**
   * De pakketten in het midden, in blokken zoals de lagen in ATMIS: eerst de favorieten, dan per
   * categorie (één groep) of per groep en categorie ("Grond › Metalen", meerdere of alle groepen).
   */
  get _blokken() {
    const favorieten = this._favorieten || [];
    const zoek = this._zoek.trim().toLowerCase();
    const past = (p) => !zoek || `${p.name} ${p.code} ${p.groupName || ''}`.toLowerCase().includes(zoek);
    if (this._groep === 'FAV') {
      return this._perKop(favorieten.filter(past), p => p.groupName || p.groupCode || 'Overig', p => p.groupCode);
    }
    const binnen = (f) => this._groep !== 'SEL' || (this._filter.has(f.groupCode)
      && (this._filter.get(f.groupCode) === null || this._resultaten.some(r => r.code === f.code)));
    const favHier = favorieten.filter(f => binnen(f) && past(f));
    const rest = this._resultaten.filter(p => !this._isFavoriet(p));
    const blokken = favHier.length ? [{ titel: 'Favorieten', fav: true, items: favHier }] : [];
    const eenGroep = this._groep === 'SEL' && this._filter.size === 1;
    const kop = eenGroep
      ? (p => p.categoryName || 'Overig')
      : (p => `${p.groupName || p.groupCode || 'Overig'} › ${p.categoryName || 'Overig'}`);
    return [...blokken, ...this._perKop(rest, kop, eenGroep ? null : (p => p.groupCode))];
  }

  /**
   * Groeperen op een kop. Met `groepVan` blijven de groepen in de volgorde van de lijst links;
   * binnen een groep houden de categorieën de volgorde van de server (pakketten eerst).
   */
  _perKop(pakketten, kop, groepVan) {
    const per = new Map();
    for (const p of pakketten) {
      const k = kop(p);
      if (!per.has(k)) per.set(k, { groep: groepVan?.(p), items: [] });
      per.get(k).items.push(p);
    }
    let lijst = [...per.entries()];
    if (groepVan) {
      const plek = new Map(this._groepen.map((g, i) => [g.code, i]));
      lijst = lijst.map((e, i) => [e, i]).sort(([[, a], i], [[, b], j]) => (plek.get(a.groep) ?? 999) - (plek.get(b.groep) ?? 999) || i - j).map(([e]) => e);
    }
    return lijst.map(([titel, { items }]) => ({ titel, fav: false, items }));
  }

  _renderPakketregel(pkg, metGroep) {
    const sleutel = `${pkg.code}|${pkg.sampleTypeCode}`;
    const vast = this._inOpdracht(pkg);
    const gekozen = this._isGekozen(pkg);
    const fav = this._isFavoriet(pkg);
    return html`
      <div class="pk-rij-wrap">
        <div class="pk-rij ${gekozen ? 'gekozen' : ''} ${vast ? 'vast' : ''}" role="checkbox"
             aria-checked=${gekozen || vast ? 'true' : 'false'} aria-disabled=${vast ? 'true' : 'false'} tabindex="0"
             @click=${() => this._wisselSelectie(pkg)}
             @keydown=${(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); this._wisselSelectie(pkg); } }}>
          <span class="pk-box ${gekozen ? 'aan' : ''} ${vast ? 'vast' : ''}">${gekozen || vast ? unsafeSVG(lucideIcon(Check, 12)) : ''}</span>
          <span class="pk-naam" title=${pkg.name}>${pkg.name}</span>
          ${vast ? html`<span class="pk-label groen">In opdracht</span>` : ''}
          ${metGroep ? html`<span class="pk-label">${pkg.groupName || pkg.groupCode || ''}</span>` : ''}
          <code class="pk-code">${pkg.code}</code>
          <button class="pk-icoon vlag ${fav ? 'aan' : ''}" title=${fav ? 'Uit favorieten halen' : 'Markeer als favoriet'}
                  @click=${(e) => this._wisselFavoriet(pkg, e)}>${unsafeSVG(lucideIcon(Flag, 15))}</button>
          <button class="pk-icoon ${this._infoOpen === sleutel ? 'aan' : ''}" title="Wat zit erin?"
                  aria-expanded=${this._infoOpen === sleutel ? 'true' : 'false'}
                  @click=${(e) => this._wisselInfo(pkg, e)}>${unsafeSVG(lucideIcon(Info, 15))}</button>
        </div>
        ${this._infoOpen === sleutel ? this._renderInfo(pkg) : ''}
      </div>`;
  }

  /**
   * De kiezer, opgebouwd zoals 'Analyses zoeken' in ATMIS: links de groepen, in het midden de
   * pakketten, rechts wat je hebt aangevinkt. Je vinkt aan over groepen heen en voegt alles in één
   * keer toe. De groep staat altijd zichtbaar, want de namen zeggen op zichzelf weinig: GRN-PFAS en
   * AG3-PFAS heten allebei "PFAS (30) advieslijst 12 juli", maar het zijn andere pakketten met een
   * andere prijs.
   */
  _renderKiezer() {
    if (!this._kiezerOpen) return '';
    const opFav = this._groep === 'FAV';
    const gekozen = this._groep === 'SEL' ? [...this._filter.keys()] : [];
    const groep = gekozen.length === 1 ? this._groepen.find(g => g.code === gekozen[0]) || null : null;
    const blokken = this._blokken;
    const n = this._selectie.length;
    const perGroep = new Map();
    for (const p of this._selectie) perGroep.set(p.groupCode, (perGroep.get(p.groupCode) || 0) + 1);
    const namen = new Map();
    for (const p of this._selectie) namen.set(p.name.toLowerCase(), (namen.get(p.name.toLowerCase()) || 0) + 1);
    const plaatshouder = opFav ? 'Zoek in favorieten op naam of code'
      : groep ? `Zoek in ${groep.name} op naam of code`
      : gekozen.length ? `Zoek in ${gekozen.length} groepen op naam of code` : 'Zoek in alle groepen op naam of code';
    const leeg = opFav && !(this._favorieten || []).length
      ? 'Nog geen favorieten. Klik op het vlaggetje bij een pakket om het hier en bovenaan elke groep te zetten.'
      : this._zoek ? 'Geen pakket gevonden voor deze zoekterm.' : 'Geen pakketten in deze groep.';
    return html`
      <div class="overlay" @click=${e => { if (e.target === e.currentTarget) this._sluitKiezer(); }}>
        <div class="pk" role="dialog" aria-label="Pakketten toevoegen">
          <header class="pk-kop">
            <div>
              <strong>Pakketten toevoegen</strong>
              <span>Vink aan wat je nodig hebt en voeg alles in één keer toe aan de opdracht.</span>
            </div>
            <button class="sluit" title="Sluiten" @click=${this._sluitKiezer}>${unsafeSVG(lucideIcon(X, 18))}</button>
          </header>

          <div class="pk-lijf">
            <nav class="pk-groepen" aria-label="Groepen">
              <button class="pk-groep ${this._groep === '' ? 'aan' : ''}" @click=${() => this._kiesGroep('')}>
                <span class="pk-groep-naam">Alle groepen</span>
              </button>
              <button class="pk-groep fav ${opFav ? 'aan' : ''}" @click=${() => this._kiesGroep('FAV')}>
                <span class="pk-vlagje">${unsafeSVG(lucideIcon(Flag, 13))}</span>
                <span class="pk-groep-naam">Favorieten</span>
                <span class="pk-tel">${(this._favorieten || []).length}</span>
              </button>
              <div class="pk-navkop">Groepen</div>
              ${this._groepen.map(g => {
                const stand = this._groep === 'SEL' ? this._groepStand(g.code) : 'uit';
                const open = this._openGroepen.has(g.code);
                const cats = this._catsPerGroep.get(g.code);
                return html`
                <div class="pk-boomgroep">
                  <div class="pk-groep ${stand !== 'uit' ? 'aan' : ''}">
                    <button class="pk-pijl" title=${open ? 'Dichtklappen' : 'Openklappen'} aria-expanded=${open ? 'true' : 'false'}
                            @click=${(e) => this._wisselOpen(g.code, e)}>${unsafeSVG(lucideIcon(open ? ChevronDown : ChevronRight, 14))}</button>
                    <button class="pk-groepknop" role="checkbox" aria-checked=${stand === 'aan' ? 'true' : stand === 'half' ? 'mixed' : 'false'}
                            @click=${() => this._wisselGroep(g.code)}
                            title=${g.sampleTypeCode === this._voorkeur ? 'Past bij het monstertype uit stap 1' : ''}>
                      <span class="pk-box ${stand === 'aan' ? 'aan' : stand === 'half' ? 'half' : ''}">${stand === 'aan' ? unsafeSVG(lucideIcon(Check, 12)) : stand === 'half' ? unsafeSVG(lucideIcon(Minus, 12)) : ''}</span>
                      <span class="pk-groep-tekst">
                        <span class="pk-groep-naam">${g.name}</span>
                        <span class="pk-groep-code">${g.code}${g.sampleTypeCode === this._voorkeur ? ' · stap 1' : ''}</span>
                      </span>
                      ${perGroep.get(g.code) ? html`<span class="pk-bol">${perGroep.get(g.code)}</span>` : ''}
                    </button>
                  </div>
                  ${open ? html`
                    <!-- De tweede laag, zoals in ATMIS: de categorieën van deze groep. -->
                    <div class="pk-categorieen">
                      ${!cats ? html`<span class="pk-stil-klein">Laden…</span>`
                        : !cats.length ? html`<span class="pk-stil-klein">Geen categorieën</span>`
                        : cats.map(c => {
                          const aan = this._groep === 'SEL' && this._categorieAan(g.code, c.code);
                          return html`
                            <button class="pk-categorie ${aan ? 'aan' : ''}" role="checkbox" aria-checked=${aan ? 'true' : 'false'}
                                    @click=${() => this._wisselCategorie(g.code, c.code)}>
                              <span class="pk-box klein ${aan ? 'aan' : ''}">${aan ? unsafeSVG(lucideIcon(Check, 10)) : ''}</span>
                              <span class="pk-cat-naam" lang="nl">${c.name}</span><span class="pk-tel">${c.count}</span>
                            </button>`;
                        })}
                    </div>` : ''}
                </div>`;
              })}
            </nav>

            <div class="pk-midden">
              <div class="pk-zoekrij">
                <label class="pk-zoek">
                  ${unsafeSVG(lucideIcon(Search, 15))}
                  <input type="search" autofocus placeholder=${plaatshouder} .value=${this._zoek} @input=${e => this._typt(e.target.value)}>
                </label>
              </div>
              ${this._zoekfout ? html`<p class="fout">${this._zoekfout}</p>` : ''}
              <div class="pk-lijst">
                ${this._zoekt && !opFav ? html`<p class="pk-leeg">Zoeken…</p>`
                  : !blokken.length ? (this._zoekfout ? '' : html`<p class="pk-leeg">${leeg}</p>`)
                  : blokken.map(b => html`
                    <div class="pk-blokkop ${b.fav ? 'fav' : ''}">
                      ${b.fav ? unsafeSVG(lucideIcon(Flag, 12)) : ''}<span>${b.titel}</span><span class="pk-blokaantal">${b.items.length}</span>
                    </div>
                    ${b.items.map(pkg => this._renderPakketregel(pkg, !groep))}`)}
              </div>
            </div>

            <aside class="pk-selectie">
              <div class="pk-selectie-kop">
                <span>Selectie</span>
                <span class="pk-bol ${n ? '' : 'leeg'}">${n}</span>
                <span class="spacer"></span>
                ${n ? html`<button class="pk-wis" @click=${() => { this._selectie = []; }}>Wis alles</button>` : ''}
              </div>
              <div class="pk-selectie-lijst">
                ${!n ? html`<p class="pk-stil">Nog niets gekozen. Vink pakketten aan in de lijst; je kunt tussen groepen wisselen zonder dat je selectie verdwijnt.</p>` : ''}
                ${this._selectie.map(p => {
                  const dubbel = namen.get(p.name.toLowerCase()) > 1;
                  return html`
                    <div class="pk-gekozen">
                      <div class="pk-gekozen-tekst">
                        <span class="pk-gekozen-naam">${p.name}</span>
                        <span class="pk-gekozen-meta"><code>${p.code}</code><span class="pk-label ${dubbel ? 'let-op' : ''}">${p.groupName || p.groupCode || ''}</span></span>
                        ${dubbel ? html`<span class="pk-let-op">Zelfde naam in een andere groep gekozen</span>` : ''}
                      </div>
                      <button class="pk-weg" title="Uit selectie halen" @click=${() => this._wisselSelectie(p)}>${unsafeSVG(lucideIcon(X, 14))}</button>
                    </div>`;
                })}
              </div>
            </aside>
          </div>

          ${n ? html`
            <!-- Op een smal scherm is er geen ruimte voor het zijpaneel: dan staat de selectie hier. -->
            <div class="pk-chips">
              ${this._selectie.map(p => html`
                <span class="pk-chip">${p.name}<code>${p.code}</code>
                  <button title="Uit selectie halen" @click=${() => this._wisselSelectie(p)}>${unsafeSVG(lucideIcon(X, 12))}</button></span>`)}
            </div>` : ''}

          <footer class="pk-voet">
            <span class="pk-stil-voet">De pakketten komen als kolom in de tabel; vink ze daar per mengmonster aan.</span>
            <span class="spacer"></span>
            <button class="pk-annuleer" @click=${this._sluitKiezer}>Annuleren</button>
            <button class="pk-toevoegen" ?disabled=${!n} @click=${() => this._voegSelectieToe()}>
              ${n === 0 ? 'Pakketten toevoegen' : n === 1 ? '1 pakket toevoegen' : `${n} pakketten toevoegen`}
            </button>
          </footer>
        </div>
      </div>
    `;
  }

  static styles = css`${unsafeCSS(base)}
    :host { display:block; }
    .paneel { border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface); overflow:hidden; }
    .kop { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:16px 20px 14px;
           border-bottom:1px solid var(--mv-border-soft); }
    /* Donkergroen, net als de koppen 'Veldgegevens' en 'Mengmonsters' bij Samenstelling. */
    .kop { background:#22371f; border-bottom:none; padding:14px 20px 12px; }
    .kop .mv-caps { color:#a9c69f; font-size:.66rem; }
    .kop .telling { color:#cfe0c9; font-weight:700; font-size:.72rem; line-height:1.3; }
    .mv-caps { font-size:.7rem; font-weight:900; letter-spacing:.08em; color:var(--mv-text-sub); text-transform:uppercase; }
    .uitleg, .telling { font-size:.75rem; color:var(--mv-text-muted); }
    .spacer { flex:1; }
    .fout { margin:10px 16px 0; padding:6px 10px; border-radius:6px; background:var(--mv-danger-bg);
            color:var(--mv-danger); font-size:.78rem; font-weight:700; }
    .hint { margin:10px 16px 14px; font-size:.78rem; color:var(--mv-text-muted); }
    .leeg { display:flex; flex-direction:column; align-items:center; gap:6px; padding:44px 20px; text-align:center; }
    .leeg strong { font-size:.88rem; color:var(--mv-text); }
    .leeg span { font-size:.78rem; color:var(--mv-text-muted); max-width:420px; }

    .tabelvak { overflow-x:auto; }
    /* Niet uitrekken maar zo breed als hij nodig heeft: met width:100% kneep de browser de
       kolommen samen tot het mengmonster op één letter stond en de knop uit beeld liep. Nu
       schuift de tabel opzij, en blijven de eerste en de laatste kolom staan. */
    table { width:max-content; min-width:100%; border-collapse:separate; border-spacing:0; }
    th, td { padding:10px 12px; text-align:left; border-bottom:1px solid var(--mv-border-soft); vertical-align:middle; }
    th { font-size:.68rem; font-weight:800; letter-spacing:.04em; color:var(--mv-text-sub);
         text-transform:uppercase; background:var(--mv-surface-alt); white-space:nowrap; }

    /* Links het mengmonster, rechts de knop: allebei blijven ze staan tijdens het schuiven.
       De schaduw aan hun binnenrand verschijnt pas als er werkelijk iets achter schuift;
       zie _meetSchuif. */
    th.mm, td.mm { position:sticky; left:0; z-index:2; min-width:275px; }
    th.toevoegen, td.toevoegen { position:sticky; right:0; z-index:2; }
    .tabelvak.links-verborgen :is(th, td).mm { box-shadow:6px 0 8px -7px rgba(16,28,18,.35); }
    .tabelvak.rechts-verborgen :is(th, td).toevoegen { box-shadow:-6px 0 8px -7px rgba(16,28,18,.35); }
    td.mm, td.toevoegen { background:var(--mv-surface); }
    th.mm, th.toevoegen { z-index:3; }
    /* Kop en vinkje delen dezelfde vaste kolombreedte, anders schuift het vinkje op zodra de
       ene pakketnaam langer is dan de andere. */
    th.pakket, td.vink { width:140px; min-width:140px; text-align:center; }
    th.pakket { position:relative; }
    /* Pas zichtbaar als je de kolom aanwijst: een rij kruisjes boven de matrix nodigt uit om
       er per ongeluk een aan te tikken, en weghalen is niet wat je hier meestal komt doen. */
    .kolomweg { position:absolute; top:3px; right:3px; display:flex; padding:2px; border:none;
                border-radius:4px; background:none; color:var(--mv-text-muted); cursor:pointer;
                opacity:0; transition:opacity .12s ease; }
    th.pakket:hover .kolomweg, .kolomweg:focus-visible { opacity:1; }
    .kolomweg:hover { background:var(--mv-danger-bg); color:var(--mv-danger); }
    /* Het mengmonster dat in Samenstelling gevuld wordt, licht groen: zo zie je waar je
       potjes heen gaan als je terugschakelt. */
    .mmkop { display:flex; align-items:center; gap:8px; }
    .stip { width:7px; height:7px; flex:none; border-radius:50%; background:var(--mv-primary); }
    .stip.rood { background:var(--mv-danger); }
    .mmnaam { border:none; background:none; padding:0; font:inherit; font-size:.88rem; font-weight:800; color:var(--mv-text); }
    button.mmnaam { cursor:text; }
    button.mmnaam:hover { text-decoration:underline dotted; text-underline-offset:3px; }
    .naamveld { width:200px; box-sizing:border-box; padding:4px 8px; border:1.5px solid var(--mv-primary);
                border-radius:6px; background:var(--mv-surface); color:var(--mv-text);
                font:inherit; font-size:.86rem; font-weight:800; outline:none; }
    .naamfout { margin-top:4px; font-size:.7rem; font-weight:700; color:var(--mv-danger); }
    .notitie { display:flex; padding:2px; border:none; border-radius:4px; background:none;
               color:var(--mv-text-muted); cursor:pointer; }
    .notitie.gevuld { color:var(--mv-primary); }
    .notitie:hover { background:var(--mv-bg); color:var(--mv-text-sub); }
    .waarsch { margin:6px 0 0; padding:4px 8px 4px 20px; border-radius:6px; background:var(--mv-danger-bg);
               color:var(--mv-danger); font-size:.66rem; font-weight:700; line-height:1.4; max-width:420px; white-space:normal; }
    /* Midden in de laatste kolom, recht onder '+ Pakket'. */
    /* '+ Pakket' midden in de laatste kolom; de prullenbak helemaal rechts, als actie op de rij. */
    th.toevoegen { text-align:center; }
    td.toevoegen { text-align:right; }
    .weg { display:flex; margin:0 0 0 auto; padding:4px; border:none; border-radius:6px; background:none; color:var(--mv-text-muted); cursor:pointer; }
    .weg:hover { background:var(--mv-danger-bg); color:var(--mv-danger); }
    .tekstknop.groen { color:var(--mv-primary); font-size:.76rem; font-weight:800; white-space:nowrap; text-transform:none; letter-spacing:0; }
    .tekstknop.groen:hover { color:var(--mv-primary-hover); }
    .voet { display:flex; align-items:center; gap:12px; padding:14px 16px; border-top:1px solid var(--mv-border-soft); }
    .hoofdknop { display:inline-flex; align-items:center; gap:8px; padding:9px 15px; border:none; border-radius:9px;
                 background:var(--mv-primary); color:#fff; font:inherit; font-size:.82rem; font-weight:800; cursor:pointer; }
    .hoofdknop:hover { background:var(--mv-primary-hover); }
    .notitie-venster textarea { width:100%; box-sizing:border-box; padding:8px 10px; border-radius:8px;
                                border:1px solid var(--mv-border); background:var(--mv-bg);
                                font:inherit; font-size:.82rem; resize:vertical; }
    .notitieknoppen { display:flex; align-items:center; justify-content:flex-end; gap:12px; margin-top:10px; }
    .tekstknop { border:none; background:none; padding:0; font:inherit; font-size:.76rem;
                 font-weight:700; color:var(--mv-text-sub); cursor:pointer; }
    .slot { font-size:.66rem; font-weight:800; color:var(--mv-text-muted); }
    .knop.vol { border-style:solid; border-color:var(--mv-primary); background:var(--mv-primary); color:#fff; }
    .knop.vol:hover { background:#2f6122; }
    /* Breed genoeg voor drie potjes naast elkaar; smaller worden ze niet, dan scrolt de rest. */
    .potjes { display:flex; flex-wrap:wrap; gap:4px; margin-top:6px; max-width:420px; }
    .potje { font:inherit; font-size:.64rem; font-weight:800; padding:2px 7px; border-radius:5px;
             background:var(--mv-primary-tint); border:1px solid var(--mv-border); color:var(--mv-primary-hover); cursor:default; }
    .potjes small { font-size:.68rem; color:var(--mv-text-muted); font-style:italic; }
    td.matrixkolom { font-size:.8rem; color:var(--mv-text-sub); }

    /* De kolomkop is ook een knop: hetzelfde pakket op alles zetten is de normale handeling,
       en dat twintig keer los aanvinken is werk dat de computer hoort te doen. */
    .kolomknop { display:flex; width:100%; flex-direction:column; align-items:center; gap:1px; border:none;
                 background:none; padding:2px 4px; border-radius:6px; font:inherit; cursor:pointer;
                 color:var(--mv-text); text-transform:none; letter-spacing:0; }
    .kolomknop:hover:not(:disabled) { background:var(--mv-surface); }
    .pakketnaam { font-size:.76rem; font-weight:800; max-width:100%; overflow:hidden;
                  text-overflow:ellipsis; white-space:nowrap; }
    .kolomknop code { font-size:.62rem; font-weight:600; color:var(--mv-text-muted); }
    /* Een pakket uit de bouwfase: wel een naam, geen labcode. Het lab kan er niets mee, dus
       het valt op tot je het vervangt. */
    .pakket.zondercode { background:var(--mv-danger-bg); }
    .pakket.zondercode .kolomknop code { color:var(--mv-danger); }

    /* Uit is lichtgrijs met een duidelijke rand: wit op een witte rij was nauwelijks te zien,
       en dan lijkt een lege matrix op een kapotte matrix. */
    .vinkje { display:block; margin:0 auto; width:20px; height:20px; border-radius:5px;
              border:1.5px solid var(--mv-border-strong, #d5dbe0); background:var(--mv-bg); cursor:pointer; }
    .vinkje:hover:not(:disabled) { border-color:var(--mv-primary); background:rgba(75,157,53,.08); }
    .vinkje.aan { background:var(--mv-primary); border-color:var(--mv-primary);
                  background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m5 12.5 4.5 4.5L19 7'/%3E%3C/svg%3E");
                  background-size:13px; background-position:center; background-repeat:no-repeat; }
    .vinkje:disabled { opacity:.5; cursor:default; }
    /* Het vinkje in de kolomkop: alles aan of uit. Half = bij een deel van de analysemonsters. */
    .kolomvinkje { margin-top:5px; width:18px; height:18px; }
    .kolomvinkje.aan { background:var(--mv-primary); border-color:var(--mv-primary);
                       background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m5 12.5 4.5 4.5L19 7'/%3E%3C/svg%3E");
                       background-size:12px; background-position:center; background-repeat:no-repeat; }
    .kolomvinkje.half { background:var(--mv-primary); border-color:var(--mv-primary);
                        background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.4' stroke-linecap='round'%3E%3Cpath d='M6 12h12'/%3E%3C/svg%3E");
                        background-size:12px; background-position:center; background-repeat:no-repeat; }
    .kolomknop:hover .kolomvinkje { border-color:var(--mv-primary); }

    .knop { display:inline-flex; align-items:center; gap:6px; padding:5px 10px; border-radius:8px;
            border:1.5px dashed #b7c9b1; background:var(--mv-surface); color:var(--mv-primary);
            font:inherit; font-size:.72rem; font-weight:800; cursor:pointer; white-space:nowrap;
            text-transform:none; letter-spacing:0; }
    .knop:hover { border-color:var(--mv-primary); background:rgba(75,157,53,.05); }

    .overlay { position:fixed; inset:0; background:rgba(15,23,42,.45); display:flex;
               align-items:flex-start; justify-content:center; padding:8vh 2vw; z-index:1200; }
    /* De zoeklijst hangt bovenaan omdat hij lang wordt; een notitie van vijf regels hoort
       gewoon in het midden. */
    .overlay:has(.notitie-venster) { align-items:center; padding:3vh 2vw; }
    .venster { width:min(560px, 100%); max-height:80vh; display:flex; flex-direction:column;
               background:var(--mv-surface); border-radius:12px; box-shadow:0 24px 60px rgba(0,0,0,.25); }
    .venster header { display:flex; align-items:flex-start; gap:10px; padding:14px 16px;
                      border-bottom:1px solid var(--mv-border); }
    .venster header strong { display:block; font-size:.95rem; }
    .venster header span { font-size:.72rem; color:var(--mv-text-muted); }
    .sluit { margin-left:auto; border:none; background:none; color:var(--mv-text-sub); cursor:pointer; display:flex; }
    .venster-lijf { padding:14px 16px 16px; overflow:auto; }
    .zoekrij { display:flex; gap:8px; }
    /* Opmaak van beide velden: .mv-field en .mv-select uit base.css, zoals elders. Deze keuzelijst
       had eigen regels zonder appearance:none, waardoor de browser zijn eigen pijltje tegen de
       rechterrand tekende. Hier alleen nog de maat. */
    .zoek { flex:1; min-width:0; box-sizing:border-box; font-size:.82rem; }
    .zoekrij select { max-width:220px; box-sizing:border-box; font-size:.82rem; font-weight:700; cursor:pointer; }
    /* Plakt tegen de bovenrand van het scrollvak: top -14px compenseert de opvulling van
       .venster-lijf, anders schemert de regel erboven door die strook heen. Dekkend en boven de
       regels (z-index), anders worden de pakketten er bij het scrollen overheen getekend. */
    .groep { position:sticky; top:-14px; z-index:2; padding:9px 10px 5px; background:var(--mv-surface);
             border-bottom:1px solid var(--mv-border-soft);
             font-size:.62rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase;
             color:var(--mv-text-muted); }
    .resultaten { margin-top:10px; display:flex; flex-direction:column; }
    .resultaat { display:flex; align-items:center; gap:10px; padding:8px 10px; border:none;
                 border-bottom:1px solid var(--mv-border-soft); background:none; font:inherit;
                 text-align:left; cursor:pointer; }
    .resultaat:hover { background:var(--mv-surface-alt); }
    .naam { flex:1; font-size:.82rem; font-weight:700; color:var(--mv-text); }
    .resultaat code { font-size:.68rem; font-weight:700; color:var(--mv-text-muted); }

    /* ── De pakketkiezer: groepen | pakketten | selectie ─────────────────────────────── */
    .melding { display:flex; align-items:center; gap:12px; margin:10px 16px 0; padding:7px 10px; border-radius:6px;
               background:var(--mv-warning-panel-bg, #fff7e6); color:var(--mv-warning-text, #8a5b10); font-size:.78rem; font-weight:700; }
    .melding .tekstknop { margin-left:auto; }
    .overlay:has(.pk) { padding:44px 20px; }
    .pk { width:100%; max-width:1180px; height:min(780px, calc(100vh - 88px)); display:flex; flex-direction:column;
          background:var(--mv-surface); border-radius:18px; box-shadow:0 30px 80px rgba(0,0,0,.35); overflow:hidden; }
    .pk-kop { display:flex; align-items:flex-start; gap:16px; padding:20px 24px 16px; border-bottom:1px solid var(--mv-border-soft); }
    .pk-kop strong { display:block; font-size:1.19rem; font-weight:700; letter-spacing:-.02em; color:var(--mv-text); }
    .pk-kop span { display:block; margin-top:3px; font-size:.78rem; color:var(--mv-text-muted); }
    .pk-kop .sluit { width:32px; height:32px; align-items:center; justify-content:center; border-radius:8px; }
    .pk-kop .sluit:hover { background:var(--mv-surface-alt); }

    .pk-lijf { flex:1; min-height:0; display:grid; grid-template-columns:230px minmax(0,1fr) 290px; overflow:hidden; }
    .pk-groepen { border-right:1px solid var(--mv-border-soft); background:var(--mv-surface-alt); overflow:auto; min-height:0;
                  padding:10px 8px; display:flex; flex-direction:column; gap:2px; }
    .pk-groep { display:flex; align-items:center; gap:8px; text-align:left; border:none; cursor:pointer; padding:8px 10px;
                border-radius:8px; background:transparent; font:inherit; color:var(--mv-text); }
    .pk-groep:hover { background:var(--mv-primary-tint); }
    .pk-groep.aan { background:var(--mv-primary-tint); }
    .pk-groep.aan .pk-groep-naam { color:var(--mv-primary-hover, var(--mv-primary)); }
    .pk-groep.fav.aan { background:#faedd2; }
    .pk-groep.fav.aan .pk-groep-naam { color:#8a5b10; }
    .pk-groep-tekst { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
    .pk-groep-naam { flex:1; font-size:.81rem; font-weight:700; line-height:1.3; overflow-wrap:anywhere; hyphens:auto; }
    .pk-groep-code { font-size:.66rem; font-weight:700; color:var(--mv-text-muted); letter-spacing:.03em; }
    .pk-vlagje { display:flex; color:#d9a21b; }
    .pk-vlagje svg { fill:#d9a21b; }
    .pk-tel { font-size:.69rem; font-weight:700; color:var(--mv-text-muted); }
    .pk-scheiding { height:1px; background:var(--mv-border-soft); margin:6px 4px; }
    .pk-navkop { padding:10px 10px 4px; font-size:.62rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:var(--mv-text-muted); }
    /* De boom: groep (openklappen + vinkje) met daaronder de categorieën. */
    .pk-boomgroep .pk-groep { padding:0; gap:0; cursor:default; }
    .pk-boomgroep .pk-groep:hover { background:transparent; }
    .pk-pijl { flex:none; width:22px; align-self:stretch; display:flex; align-items:center; justify-content:center; border:none;
               background:none; border-radius:6px; color:var(--mv-text-muted); cursor:pointer; padding:0; }
    .pk-pijl:hover { background:var(--mv-primary-tint); color:var(--mv-text); }
    .pk-groepknop { flex:1; min-width:0; display:flex; align-items:center; gap:8px; text-align:left; border:none; background:none;
                    padding:8px 8px 8px 4px; border-radius:8px; font:inherit; color:var(--mv-text); cursor:pointer; }
    .pk-groepknop:hover { background:var(--mv-primary-tint); }
    .pk-groepknop .pk-box { align-self:flex-start; margin-top:1px; }
    .pk-box.half { border-color:var(--mv-primary); background:var(--mv-primary); }
    .pk-box.klein { width:15px; height:15px; border-radius:4px; }
    .pk-categorieen { display:flex; flex-direction:column; gap:1px; margin:0 0 6px 30px; padding-left:6px; border-left:1px solid var(--mv-border); }
    .pk-categorie { display:flex; align-items:center; gap:8px; text-align:left; border:none; background:transparent; padding:5px 8px;
                    border-radius:6px; font:inherit; font-size:.76rem; font-weight:600; color:var(--mv-text-sub); cursor:pointer; }
    .pk-categorie:hover { background:var(--mv-primary-tint); }
    .pk-categorie.aan { color:var(--mv-text); }
    /* Lange namen (Korrelgrootteverdeling) afbreken in plaats van het aantal uit het vak te duwen;
       met lang="nl" zet de browser het koppelteken op een goede plek. */
    .pk-categorie { min-width:0; }
    .pk-cat-naam { flex:1; min-width:0; overflow-wrap:anywhere; hyphens:auto; }
    .pk-categorie .pk-tel, .pk-categorie .pk-box { flex:none; }
    .pk-stil-klein { padding:4px 8px; font-size:.72rem; color:var(--mv-text-muted); }
    .pk-bol { min-width:20px; height:20px; padding:0 6px; box-sizing:border-box; border-radius:999px; background:var(--mv-primary);
              color:#fff; font-size:.69rem; font-weight:800; display:inline-flex; align-items:center; justify-content:center; }
    .pk-bol.leeg { background:#c4c8d4; }

    .pk-midden { min-width:0; min-height:0; display:flex; flex-direction:column; overflow:hidden; }
    .pk-zoekrij { padding:14px 18px 10px; display:flex; flex-wrap:wrap; gap:8px; border-bottom:1px solid var(--mv-border-soft); }
    .pk-zoek { flex:1; min-width:180px; position:relative; display:flex; align-items:center; color:var(--mv-text-muted); }
    .pk-zoek svg { position:absolute; left:12px; pointer-events:none; }
    .pk-zoek input { width:100%; box-sizing:border-box; padding:10px 12px 10px 34px; border:1px solid var(--mv-border-soft);
                     border-radius:8px; background:var(--mv-bg); color:var(--mv-text); font:inherit; font-size:.81rem; outline:none; }
    .pk-zoek input:focus { border-color:var(--mv-primary); background:var(--mv-surface); }
    .pk-select { flex:0 1 220px; min-width:160px; padding:10px 12px; border:1px solid var(--mv-border-soft); border-radius:8px;
                 background:var(--mv-bg); color:var(--mv-text); font:inherit; font-size:.81rem; font-weight:700; cursor:pointer; }
    .pk-lijst { flex:1; min-height:0; overflow:auto; padding:0 18px 18px; }
    .pk-leeg { margin:28px 4px; font-size:.81rem; color:var(--mv-text-muted); }
    /* Dekkend en boven de regels, anders schuiven de pakketten er bij het scrollen doorheen. */
    .pk-blokkop { position:sticky; top:0; z-index:2; background:var(--mv-surface); padding:14px 10px 6px; display:flex;
                  align-items:center; gap:6px; font-size:.66rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase;
                  color:var(--mv-text-muted); }
    .pk-blokkop.fav { color:#8a5b10; }
    .pk-blokkop.fav svg { fill:#d9a21b; color:#d9a21b; }
    .pk-blokaantal { color:#c4c8d4; font-weight:700; }
    .pk-rij-wrap { border-bottom:1px solid var(--mv-border-soft); }
    .pk-rij { display:flex; align-items:center; gap:12px; padding:9px 10px; border-radius:8px; cursor:pointer; outline:none; }
    .pk-rij:hover, .pk-rij:focus-visible { background:var(--mv-surface-alt); }
    .pk-rij.gekozen { background:var(--mv-primary-tint); }
    .pk-rij.vast { cursor:default; }
    .pk-rij.vast .pk-naam { color:var(--mv-text-muted); }
    .pk-box { width:18px; height:18px; flex:none; box-sizing:border-box; border-radius:5px; border:1.5px solid #cdd3da;
              background:var(--mv-surface); display:flex; align-items:center; justify-content:center; color:#fff; }
    .pk-box.aan { border-color:var(--mv-primary); background:var(--mv-primary); }
    .pk-box.vast { border-color:#c9d9c3; background:#c9d9c3; }
    .pk-naam { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:.84rem; font-weight:700; color:var(--mv-text); }
    .pk-label { flex:none; font-size:.69rem; font-weight:700; color:var(--mv-text-sub); background:var(--mv-surface-alt);
                padding:2px 7px; border-radius:5px; white-space:nowrap; }
    .pk-label.groen { color:var(--mv-primary-hover, var(--mv-primary)); background:var(--mv-primary-tint); }
    .pk-label.let-op { color:#8a5b10; background:#faedd2; }
    .pk-code { flex:none; min-width:86px; text-align:right; font-size:.69rem; font-weight:500; color:#8e93a6; }
    .pk-icoon { width:28px; height:28px; flex:none; border:none; background:none; border-radius:6px; display:flex;
                align-items:center; justify-content:center; cursor:pointer; color:#a1a5b7; }
    .pk-icoon:hover { background:var(--mv-surface-alt); }
    .pk-icoon.aan { color:var(--mv-primary); }
    .pk-icoon.vlag { color:#c4c8d4; }
    .pk-icoon.vlag:hover { background:#faedd2; }
    .pk-icoon.vlag.aan { color:#d9a21b; }
    .pk-icoon.vlag.aan svg { fill:#d9a21b; }
    .pk-info { margin:0 10px 10px 40px; background:var(--mv-surface-alt); border-radius:8px; padding:10px 12px; }
    .pk-info-kop { font-size:.66rem; font-weight:800; letter-spacing:.07em; text-transform:uppercase; color:var(--mv-text-muted); margin-bottom:6px; }
    .pk-bepalingen { display:grid; grid-template-columns:repeat(auto-fill, minmax(220px, 1fr)); gap:2px 16px; }
    .pk-bepaling { display:flex; gap:10px; font-size:.78rem; padding:3px 0; color:var(--mv-text); }
    .pk-bepaling code { font-size:.69rem; color:#8e93a6; min-width:80px; }
    .pk-voorbewerking { margin-top:6px; font-size:.75rem; color:var(--mv-text-sub); }
    .pk-info-noot { font-size:.72rem; font-style:italic; color:var(--mv-text-muted); margin-top:6px; }
    .pk-stil { margin:8px 6px; font-size:.78rem; line-height:1.5; color:var(--mv-text-muted); }
    .pk-info .pk-stil { margin:0; }
    .pk-stil-donker { font-size:.78rem; color:var(--mv-text-sub); }
    .pk-fout { font-size:.78rem; font-weight:700; color:var(--mv-danger); }

    .pk-selectie { border-left:1px solid var(--mv-border-soft); background:var(--mv-surface-alt); display:flex; flex-direction:column;
                   min-width:0; min-height:0; overflow:hidden; }
    .pk-selectie-kop { padding:16px 16px 10px; display:flex; align-items:center; gap:8px; font-size:.69rem; font-weight:800;
                       letter-spacing:.07em; text-transform:uppercase; color:var(--mv-text-sub); }
    .pk-wis { border:none; background:none; padding:0; font:inherit; font-size:.75rem; font-weight:700; text-transform:none;
              letter-spacing:0; color:var(--mv-text-sub); cursor:pointer; }
    .pk-wis:hover { color:var(--mv-danger); }
    .pk-selectie-lijst { flex:1; min-height:0; overflow:auto; padding:0 10px 12px; display:flex; flex-direction:column; gap:6px; }
    .pk-gekozen { display:flex; align-items:flex-start; gap:8px; padding:9px 10px; background:var(--mv-surface);
                  border:1px solid var(--mv-border-soft); border-radius:9px; }
    .pk-gekozen-tekst { flex:1; min-width:0; display:flex; flex-direction:column; gap:3px; }
    .pk-gekozen-naam { font-size:.81rem; font-weight:700; line-height:1.3; color:var(--mv-text); }
    .pk-gekozen-meta { display:flex; gap:6px; align-items:center; flex-wrap:wrap; }
    .pk-gekozen-meta code { font-size:.66rem; color:#8e93a6; }
    .pk-let-op { font-size:.69rem; font-weight:600; color:#8a5b10; }
    .pk-weg { width:24px; height:24px; flex:none; border:none; background:none; border-radius:6px; color:#a1a5b7;
              display:flex; align-items:center; justify-content:center; cursor:pointer; }
    .pk-weg:hover { background:var(--mv-danger-bg); color:var(--mv-danger); }

    .pk-chips { display:none; border-top:1px solid var(--mv-border-soft); padding:10px 24px; flex-wrap:wrap; gap:6px; max-height:96px; overflow:auto; }
    .pk-chip { display:inline-flex; align-items:center; gap:6px; padding:4px 4px 4px 9px; background:var(--mv-primary-tint);
               border-radius:7px; font-size:.75rem; font-weight:700; color:var(--mv-primary-hover, var(--mv-primary)); }
    .pk-chip code { font-size:.66rem; font-weight:500; opacity:.75; }
    .pk-chip button { width:18px; height:18px; border:none; background:none; border-radius:4px; color:inherit; display:flex;
                      align-items:center; justify-content:center; cursor:pointer; padding:0; }
    /* Smal scherm: geen ruimte voor drie kolommen; de selectie gaat naar een balk onderaan. */
    @media (max-width: 999px) {
      .pk-lijf { grid-template-columns:190px minmax(0,1fr); }
      .pk-selectie { display:none; }
      .pk-chips { display:flex; }
    }
    @media (min-width: 1000px) and (max-width: 1199px) {
      .pk-lijf { grid-template-columns:225px minmax(0,1fr) 230px; }
    }

    .pk-voet { border-top:1px solid var(--mv-border-soft); padding:14px 24px; display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
    .pk-stil-voet { font-size:.78rem; color:var(--mv-text-muted); }
    .pk-annuleer { border:none; background:none; font:inherit; font-size:.88rem; font-weight:700; color:var(--mv-text-sub); cursor:pointer; padding:10px 8px; }
    .pk-toevoegen { display:inline-flex; align-items:center; gap:8px; padding:10px 18px; border:none; border-radius:9px;
                    background:var(--mv-primary); color:#fff; font:inherit; font-size:.88rem; font-weight:700; white-space:nowrap; cursor:pointer; }
    .pk-toevoegen:hover:not(:disabled) { background:var(--mv-primary-hover); }
    .pk-toevoegen:disabled { background:#b7d9ac; cursor:default; }
    .groepnaam { font-size:.6rem; font-weight:700; color:var(--mv-text-muted); text-transform:none; letter-spacing:0; }
    .soort-fout { font-size:.72rem; font-weight:800; color:var(--mv-danger); }
    /* Kan niet bij dit analysemonster: doorgestreept vlak in plaats van een leeg vinkje. */
    .vinkje.past-niet { background:repeating-linear-gradient(45deg, var(--mv-bg), var(--mv-bg) 3px, var(--mv-border-soft) 3px, var(--mv-border-soft) 5px);
                        border-style:dashed; opacity:.7; cursor:not-allowed; }
  `;
}

customElements.define('lab-pakketten', LabPakketten);
